<template>
  <iColumn
    width="fill"
    height="fill"
    position="absolute"
    :position-placements="{top: '0', bottom: '0', left: '0', right: '0'}"
    :z-index="50"
    data-test="prejoin-lobby"
  >
    <iColumn
      v-if="isInFloatingWindow"
      width="fill"
      height="fill"
      align="center"
      wrap="nowrap"
      :gap="isNarrowWindow ? 'small' : 'standard'"
      :min-height="0"
      data-test="prejoin-window"
      :style-overrides="{
        paddingTop: isNarrowWindow ? 'small' : 'standard',
        paddingBottom: isNarrowWindow ? 'small' : 'standard',
        paddingLeft: isNarrowWindow ? 'small' : 'standard',
        paddingRight: isNarrowWindow ? 'small' : 'standard',
      }"
    >
      <iColumn
        gap="none"
        align="center"
        width="fill"
        data-test="prejoin-window-title"
      >
        <iHeading font-weight="dark" :font-size="isNarrowWindow ? 'standard' : 'large'" :wrap="false">
          {{ title }}
        </iHeading>
        <iText
          v-if="!isNarrowWindow"
          font-size="small"
          variant="subtle"
          :wrap="false"
          data-test="prejoin-presence"
        >
          {{ presenceCopy }}
        </iText>
      </iColumn>

      <iCallToast
        v-if="notice"
        icon="ic:outline-info"
        data-test="prejoin-notice"
      >
        {{ notice }}
      </iCallToast>

      <iColumn
        width="fill"
        height="fill"
        align="center"
        vertical-align="middle"
        overflow="hidden"
        :min-height="0"
        data-test="prejoin-preview"
        position="relative"
        :style-overrides="{
          borderRadius: 'large',
          backgroundColor: 'subtle',
        }"
      >
        <MediaTrackRenderer
          v-if="hasCameraPreview"
          fit="cover"
          :stream="cameraTile?.stream || null"
          :video-track="cameraTile?.videoTrack || null"
          :is-muted="true"
          :flip-horizontally="isLocalVideoMirrored"
        />
        <iVideoTileAvatar
          v-else
          :name="displayName"
          :src="avatarUrl"
          :message="previewStatusCopy"
          data-test="tile-avatar"
        />
      </iColumn>

      <iRow
        align="center"
        vertical-align="middle"
        wrap="nowrap"
        gap="small"
        width="fill"
        data-test="prejoin-window-bar"
      >
        <iRow
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          width="hug"
          data-test="prejoin-controls"
        >
          <CallControlButton
            :size="isNarrowWindow ? 'compact' : 'standard'"
            :variant="isMicrophoneEnabled ? 'standard' : 'alert'"
            :label="isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'"
            @click="$emit('toggle-mic')"
          >
            <iIcon :name="isMicrophoneEnabled ? 'ic:baseline-mic' : 'ic:baseline-mic-off'" variant="light" />
          </CallControlButton>
          <CallControlButton
            :size="isNarrowWindow ? 'compact' : 'standard'"
            :variant="isCameraEnabled ? 'standard' : 'alert'"
            :label="isCameraEnabled ? 'Turn camera off' : 'Turn camera on'"
            @click="$emit('toggle-camera')"
          >
            <iIcon :name="isCameraEnabled ? 'ic:baseline-videocam' : 'ic:baseline-videocam-off'" variant="light" />
          </CallControlButton>
          <CallControlButton
            :size="isNarrowWindow ? 'compact' : 'standard'"
            :is-toggle="false"
            label="More audio and video settings"
            data-test="prejoin-settings"
            @click="$emit('open-settings')"
          >
            <iIcon name="ic:outline-settings" variant="light" />
          </CallControlButton>
        </iRow>
        <iCallCta
          variant="filled"
          :loading="isJoining"
          data-test="prejoin-join"
          @click="$emit('join')"
        >
          {{ isJoining ? "Joining…" : joinLabel }}
        </iCallCta>
      </iRow>
    </iColumn>
    <iColumn
      v-else
      width="fill"
      height="fill"
      gap="none"
      wrap="nowrap"
      :min-height="0"
      data-test="prejoin-page"
      :data-layout="isWide ? 'wide' : 'narrow'"
    >
      <iCallLobby
        :title="eventName || title"
        :eyebrow="roomContext.whenCopy || ''"
        :meta="roomContext.hostName ? `Hosted by ${roomContext.hostName}` : ''"
        :presence="presenceCopy"
        :device-summary="deviceSummary"
        :form-hint="isGuest ? 'The host lets you in. Without a name you join as a guest.' : ''"
        :identity-name="displayName"
      >
        <template #preview>
          <iVideoTile
            :aspect="isPhoneWidth ? '4:5' : '16:9'"
            fit="cover"
            :label="displayName"
            caption-placement="top"
            data-test="prejoin-preview"
          >
            <MediaTrackRenderer
              v-if="hasCameraPreview"
              :stream="cameraTile?.stream || null"
              :video-track="cameraTile?.videoTrack || null"
              :is-muted="true"
              :flip-horizontally="isLocalVideoMirrored"
            />
            <iVideoTileAvatar
              v-else
              :name="displayName"
              :src="avatarUrl"
              :message="previewStatusCopy"
              data-test="tile-avatar"
            />
          </iVideoTile>
        </template>

        <template #dock>
          <PreJoinDock
            :is-microphone-enabled="isMicrophoneEnabled"
            :is-camera-enabled="isCameraEnabled"
            :is-speaking="isSpeaking"
            :available-audio-input-devices="availableAudioInputDevices"
            :available-output-devices="availableOutputDevices"
            :available-video-devices="availableVideoDevices"
            :selected-microphone-device-id="selectedMicrophoneDeviceId"
            :selected-output-device-id="selectedOutputDeviceId"
            :selected-camera-device-id="selectedCameraDeviceId"
            @toggle-mic="$emit('toggle-mic')"
            @toggle-camera="$emit('toggle-camera')"
            @change-microphone="$emit('change-microphone', $event)"
            @change-output="$emit('change-output', $event)"
            @change-camera="$emit('change-camera', $event)"
            @open-settings="$emit('open-settings')"
          />
        </template>

        <template v-if="isMicrophoneEnabled" #meter>
          <iRow
            gap="thin"
            vertical-align="middle"
            wrap="nowrap"
            width="hug"
            data-test="prejoin-mic-level"
          >
            <iColumn
              v-for="bar in meterBars"
              :key="bar"
              :width="3"
              :height="meterBarHeight(bar)"
              :data-lit="bar <= litBars ? '1' : '0'"
              data-test="prejoin-mic-level-bar"
              :style-overrides="{
                backgroundColor: bar <= litBars ? 'interactiveSelected' : 'disabled',
                borderRadius: 'small',
              }"
            />
          </iRow>
        </template>

        <template v-if="peopleStack.length" #people>
          <iAvatarStack :people="peopleStack" size="small" />
        </template>

        <template v-if="notice" #notice>
          <iCallToast icon="ic:outline-info" data-test="prejoin-notice">
            {{ notice }}
          </iCallToast>
        </template>

        <template v-if="isGuest" #form>
          <iTextInput
            :model-value="guestName"
            placeholder="Your name"
            width="fill"
            data-test="prejoin-guest-name"
            @update:model-value="$emit('update:guest-name', $event)"
            @enter="$emit('join')"
          />
        </template>

        <template #actions>
          <iCallCta
            variant="primary"
            block
            :loading="isJoining"
            data-test="prejoin-join"
            @click="$emit('join')"
          >
            {{ isJoining ? "Joining…" : joinLabel }}
          </iCallCta>
          <iCallCta
            v-if="canJoinAudioOnly && !isBroadcast"
            variant="outline"
            block
            :disabled="isJoining"
            data-test="prejoin-join-audio-only"
            @click="$emit('join-audio-only')"
          >
            Join with audio only
          </iCallCta>
        </template>

        <template #identity-avatar>
          <iRow width="hug" data-test="prejoin-avatar">
            <iAvatar :name="displayName" :src="avatarUrl" size="small" />
          </iRow>
        </template>

        <template #identity-action>
          <iCallCta
            v-if="isGuest"
            variant="link"
            :disabled="isJoining"
            data-test="prejoin-sign-in"
            @click="$emit('sign-in')"
          >
            Sign in
          </iCallCta>
          <iCallCta
            v-else
            variant="link"
            :disabled="isJoining"
            data-test="prejoin-back"
            @click="$emit('close')"
          >
            Back
          </iCallCta>
        </template>
      </iCallLobby>
    </iColumn>
  </iColumn>
</template>

<script>
import MediaTrackRenderer from "%/apps/video-engine/components/video-call/shared/MediaTrackRenderer.vue";
import CallControlButton from "%/apps/video-engine/components/video-call/controls/CallControlButton.vue";
import PreJoinDock from "%/apps/video-engine/components/video-call/PreJoinDock.vue";
import { describeDevices, selectedDeviceName } from "%/apps/video-engine/device-catalog";
import { litBarCount } from "%/apps/video-engine/audio/mic-level-meter";
import { LOBBY_LAYOUT_FLOATING_WINDOW, RoomContextMixin, stackPeople } from "%/apps/video-engine/room-context";
import { describePresence } from "%/dynamic-component-editor/helpers/event-presence";

const METER_BARS = 5;
const METER_BAR_HEIGHTS = [6, 10, 14, 10, 6];
const NARROW_WINDOW_BELOW_WIDTH = 440;
const WIDE_LOBBY_FROM_WIDTH = 880;
const PHONE_LOBBY_BELOW_WIDTH = 520;
const SPEAKING_FROM_BARS = 2;

export default {
  name: "PreJoinLobby",
  components: {
    MediaTrackRenderer,
    CallControlButton,
    PreJoinDock,
  },
  mixins: [RoomContextMixin],
  props: {
    title: {
      type: String,
      default: "Ready to join?",
    },
    eventTitle: {
      type: String,
      default: "",
    },
    displayName: {
      type: String,
      default: "You",
    },
    isGuest: {
      type: Boolean,
      default: false,
    },
    guestName: {
      type: String,
      default: "",
    },
    avatarUrl: {
      type: String,
      default: "",
    },
    isBroadcast: {
      type: Boolean,
      default: false,
    },
    participantCount: {
      type: Number,
      default: 0,
    },
    joinLabel: {
      type: String,
      required: false,
      default: "Join call",
    },
    canJoinAudioOnly: {
      type: Boolean,
      default: true,
    },
    notice: {
      type: String,
      default: "",
    },
    cameraTile: {
      type: Object,
      default: null,
    },
    isLocalVideoMirrored: {
      type: Boolean,
      default: true,
    },
    availableVideoDevices: {
      type: Array,
      default: () => [],
    },
    availableAudioInputDevices: {
      type: Array,
      default: () => [],
    },
    availableOutputDevices: {
      type: Array,
      default: () => [],
    },
    selectedCameraDeviceId: {
      type: String,
      default: null,
    },
    selectedMicrophoneDeviceId: {
      type: String,
      default: null,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
    micLevel: {
      type: Number,
      default: 0,
    },
    isMicrophoneEnabled: {
      type: Boolean,
      default: false,
    },
    isCameraEnabled: {
      type: Boolean,
      default: false,
    },
    isJoining: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:guest-name",
    "sign-in",
    "toggle-mic",
    "toggle-camera",
    "change-camera",
    "change-microphone",
    "change-output",
    "open-settings",
    "join",
    "join-audio-only",
    "close",
  ],
  data() {
    return { lobbyWidth: typeof window === "undefined" ? 0 : window.innerWidth };
  },
  computed: {
    isInFloatingWindow() {
      return this.roomContext.lobbyLayout === LOBBY_LAYOUT_FLOATING_WINDOW;
    },
    isNarrowWindow() {
      return this.lobbyWidth > 0 && this.lobbyWidth < NARROW_WINDOW_BELOW_WIDTH;
    },
    isWide() {
      return this.lobbyWidth >= WIDE_LOBBY_FROM_WIDTH;
    },
    isPhoneWidth() {
      return this.lobbyWidth < PHONE_LOBBY_BELOW_WIDTH;
    },
    eventName() {
      return this.roomContext.title || this.eventTitle;
    },
    deviceSummary() {
      return [
        selectedDeviceName(this.availableAudioInputDevices, "audioinput", this.selectedMicrophoneDeviceId),
        selectedDeviceName(this.availableVideoDevices, "videoinput", this.selectedCameraDeviceId),
      ].join(" · ");
    },
    peopleStack() {
      return stackPeople(this.roomContext.people);
    },
    previewStatusCopy() {
      if (!this.isCameraEnabled) {
        return "Camera is off";
      }

      return this.cameraItems.length === 0 ? "No camera found" : "Starting your camera…";
    },
    hasCameraPreview() {
      return this.isCameraEnabled && Boolean(this.cameraTile?.stream || this.cameraTile?.videoTrack);
    },
    presenceCopy() {
      return describePresence(this.participantCount, this.isBroadcast);
    },
    cameraItems() {
      return describeDevices(this.availableVideoDevices, "videoinput");
    },
    meterBars() {
      return Array.from({ length: METER_BARS }, (_, index) => index + 1);
    },
    isSpeaking() {
      return this.litBars >= SPEAKING_FROM_BARS;
    },
    litBars() {
      return this.isMicrophoneEnabled ? litBarCount(this.micLevel, METER_BARS) : 0;
    },
  },
  mounted() {
    this.lobbyWidth = Math.round(this.$el.getBoundingClientRect().width) || this.lobbyWidth;
    this.lobbyResizeObserver = new ResizeObserver(([entry]) => {
      this.lobbyWidth = Math.round(entry.contentRect.width);
    });
    this.lobbyResizeObserver.observe(this.$el);
  },
  beforeUnmount() {
    this.lobbyResizeObserver?.disconnect();
  },
  methods: {
    meterBarHeight(bar) {
      return METER_BAR_HEIGHTS[bar - 1];
    },
  },
};
</script>
